import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { ShiftPositionType } from "@/types/db/shift_positions";
import { PositionPill } from "./PositionPill";
import { PositionStatusBar } from "./PositionStatusBar";
import { calculateAggregateStats } from "./utils";
import { Visible } from "@/components/Table/types";

type PositionsSummaryProps = {
  positions: ShiftPositionType[];
  visibleFields?: Visible<string>[];
};

export function PositionsSummary({ positions, visibleFields }: PositionsSummaryProps) {
  const { colors } = useTheme();

  // Helper to check if a field is visible
  const isVisible = (fieldName: string): boolean => {
    if (!visibleFields?.length) return true; // Show all by default
    const entry = visibleFields.find((v) => v.name === fieldName);
    return entry ? entry.visible !== false : true;
  };

  // If positions field is hidden, don't render at all
  if (!isVisible("positions")) return null;

  if (positions.length === 0) return null;

  const stats = calculateAggregateStats(positions);
  const filledCount = stats.accepted + stats.invited + stats.queued + stats.complete;

  return (
    <View style={styles.positions}>
      <View style={styles.positionHeader}>
        <ThemedText style={styles.positionLabel}>Positions</ThemedText>
        <ThemedText
          style={[styles.fillRate, { color: stats.accepted === stats.total ? colors.emerald.text : colors.text }]}
        >
          {filledCount}/{stats.total}
        </ThemedText>
      </View>

      {/* Status breakdown bar */}
      {isVisible("progress") && <PositionStatusBar stats={stats} />}

      {/* Position pills */}
      <View style={styles.positionPills}>
        {positions.map((pos, i) => (
          <PositionPill key={`${pos.id} - ${i}`} position={pos} index={i} />
        ))}
        {positions.length > 3 && <ThemedText style={styles.moreText}>+{positions.length - 3}</ThemedText>}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  positions: {
    paddingHorizontal: 8,
    paddingBottom: 8,
    gap: 6,
  },
  positionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  positionLabel: {
    fontSize: 10,
    opacity: 0.6,
    textTransform: "uppercase",
  },
  fillRate: {
    fontSize: 11,
    fontWeight: "600",
  },
  fillBar: {
    height: 4,
    borderRadius: 2,
    overflow: "hidden",
  },
  fillProgress: {
    height: "100%",
    borderRadius: 2,
  },
  positionPills: {
    flexDirection: "row",
    gap: 4,
    alignItems: "center",
  },
  moreText: {
    fontSize: 10,
    opacity: 0.5,
    marginLeft: 2,
  },
});
